import { useState, useRef } from "react";
import PdfUploader from "./components/PdfUploader";
import QRGenerator from "./components/QRGenerator";
import QRControls from "./components/QRControls";
import QRDownload from "./components/QRDownload";
import ConversionBlock from "./components/ConversionBlock";
import ZaffloLogo from "../assets/fav.png"
import { Helmet } from "react-helmet-async";


export default function QRMenuGeneratorPage() {
    const previewRef = useRef();
    // 🔑 GLOBAL STATE (shared across modules)
    const [pdfUrl, setPdfUrl] = useState(null);

    const [qrSettings, setQrSettings] = useState({
        qrColor: "#000000",
        bgColor: "#ffffff",
        logo: null,
        businessName: "",
    });

    return (

        <>
            <Helmet prioritizeSeoTags>
                <title key="title">
                    Free Online QR Menu Generator | Create QR Code for Restaurant, Hotel, Cafe, Salon | Zafflo
                </title>

                <meta  key="desc"
                    name="description"
                    content="Create a free QR code for your menu in seconds. Upload your PDF and generate a print-ready QR code for restaurants, cafes, hotels, salons and spas. No signup required."
                />

                <link
                    rel="canonical"
                    href="https://zafflo.com/free-online-qr-menu-generator"
                />

                {/* Open Graph */}
                <meta property="og:title" content="Free Online QR Menu Generator | Zafflo" />
                <meta property="og:description" content="Generate QR code for your menu instantly. Free & no signup." />
                <meta property="og:url" content="https://zafflo.com/free-online-qr-menu-generator" />

                <script type="application/ld+json">
                    {`
{
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": "Free QR Menu Generator",
  "url": "https://zafflo.com/free-online-qr-menu-generator",
  "applicationCategory": "BusinessApplication",
  "description": "Generate QR codes for restaurant menus, salons and hotels instantly for free."
}
`}
                </script>

            </Helmet>

            <div className="min-h-screen ...">

                <div className="min-h-screen bg-[#0a0a0a] text-white font-sans">



                    {/* ── AMBIENT GLOW LAYER ── */}
                    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
                        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-white/[0.03] rounded-full blur-3xl" />
                        <div className="absolute top-1/3 -left-32 w-[300px] h-[300px] bg-white/[0.02] rounded-full blur-2xl" />
                    </div>

                    {/* ── TOP NAV BAR ── */}
                    <header className="relative z-10 border-b border-white/[0.06] backdrop-blur-sm">
                        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <img src={ZaffloLogo} alt="Zafflo" className="h-7 w-auto" />
                                <span className="text-white/80 text-sm font-light tracking-widest uppercase ml-1">ZAFFLO</span>
                            </div>
                            <span className="text-xs text-white/80 tracking-widest uppercase border border-white/60 rounded-full px-3 py-1">
                                Free · No Sign-up
                            </span>
                        </div>
                    </header>

                    {/* ── HERO ── */}
                    {/* H1 targets primary keyword: "Free Online QR Menu Generator" */}
                    <section className="relative z-10 text-center pt-16 pb-12 px-4">

                        <div className="inline-flex items-center gap-2 bg-white/[0.05] border border-white/10 rounded-full px-4 py-1.5 mb-6">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            <span className="text-xs text-white/60 tracking-widest uppercase font-medium">100% Free · No Sign-up Required · No Watermark</span>
                        </div>

                        {/* H1 — primary keyword exact match */}
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.1] text-white">
                            Free Online QR Menu Generator
                        </h1>

                        {/* Subheading — secondary + LSI keywords */}
                        <p className="mt-4 text-white/50 text-base md:text-lg max-w-2xl mx-auto leading-relaxed font-light">
                            Upload your PDF menu and generate a custom <strong className="text-white/70 font-normal">QR code for your restaurant, café, hotel, or salon</strong> —
                            free, in under 60 seconds. No account. No watermark. Download a print-ready
                            <strong className="text-white/70 font-normal"> contactless digital menu QR code</strong> instantly.
                        </p>

                        {/* Keyword-rich use-case tags */}
                        <div className="mt-6 flex justify-center gap-3 flex-wrap">
                            {[
                                "Restaurant QR Menu",
                                "Café Digital Menu",
                                "Hotel Room Service QR",
                                "Salon Service Menu",
                                "Bar & Lounge Menu",
                                "Spa Menu QR Code",
                            ].map((tag) => (
                                <span key={tag} className="text-xs text-white/40 border border-white/[0.08] rounded-full px-3 py-1">
                                    {tag}
                                </span>
                            ))}
                        </div>
                    </section>

                    {/* ── TOOL SECTION ── */}
                    <section className="relative z-10 max-w-6xl mx-auto px-4 pb-16">

                        <div className="flex items-center justify-center gap-6 mb-8 text-xs text-white/30 tracking-widest uppercase">
                            <span className="flex items-center gap-2">
                                <span className="w-5 h-5 rounded-full border border-white/20 flex items-center justify-center text-[10px] text-white/50">1</span>
                                Upload PDF Menu
                            </span>
                            <span className="w-8 h-px bg-white/10" />
                            <span className="flex items-center gap-2">
                                <span className="w-5 h-5 rounded-full border border-white/20 flex items-center justify-center text-[10px] text-white/50">2</span>
                                Customise QR Code
                            </span>
                            <span className="w-8 h-px bg-white/10" />
                            <span className="flex items-center gap-2">
                                <span className="w-5 h-5 rounded-full border border-white/20 flex items-center justify-center text-[10px] text-white/50">3</span>
                                Download Free
                            </span>
                        </div>

                        <div className="grid md:grid-cols-2 gap-5">

                            {/* ── LEFT PANEL ── */}
                            <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6 space-y-6 backdrop-blur-sm">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-sm font-medium text-white/80 tracking-wide uppercase">Upload & Customise</h2>
                                    <span className="text-[10px] text-white/20 border border-white/[0.06] rounded px-2 py-0.5">Step 1 – 2</span>
                                </div>

                                <div className="h-px bg-white/[0.06]" />

                                {/* MODULE 1: PDF Upload */}
                                <PdfUploader onUploadComplete={(url) => setPdfUrl(url)} />

                                <div className="h-px bg-white/[0.06]" />

                                {/* MODULE 2: QR Controls */}
                                <QRControls settings={qrSettings} setSettings={setQrSettings} />
                            </div>

                            {/* ── RIGHT PANEL ── */}
                            <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6 flex flex-col items-center justify-between backdrop-blur-sm gap-6">

                                <div className="w-full">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="text-sm font-medium text-white/80 tracking-wide uppercase">Live Preview</h2>
                                        {pdfUrl && (
                                            <span className="flex items-center gap-1.5 text-[10px] text-emerald-400/70 border border-emerald-400/20 rounded px-2 py-0.5">
                                                <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" />
                                                Ready to Download
                                            </span>
                                        )}
                                    </div>

                                    <div className="h-px bg-white/[0.06] mb-6" />

                                    <div className="text-center">
                                        {pdfUrl ? (
                                            <div className="w-full max-w-[260px] mx-auto bg-white rounded-2xl p-5 shadow-[0_0_60px_rgba(255,255,255,0.06)] ring-1 ring-white/10">
                                                <div ref={previewRef}>
                                                    <QRGenerator pdfUrl={pdfUrl} settings={qrSettings} />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="w-full max-w-[260px] mx-auto aspect-square rounded-2xl border border-dashed border-white/[0.1] flex flex-col items-center justify-center gap-3">
                                                <div className="w-10 h-10 rounded-xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center">
                                                    <svg className="w-5 h-5 text-white/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    </svg>
                                                </div>
                                                <p className="text-xs text-white/25 text-center leading-relaxed">
                                                    Upload a PDF menu to<br />generate your free QR code
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* MODULE 4: Download */}
                                {pdfUrl && (
                                    <div className="w-full max-w-[260px] mx-auto">
                                        <QRDownload previewRef={previewRef} />
                                    </div>
                                )}

                                {/* MODULE 5: Conversion CTA */}
                                <ConversionBlock />
                            </div>
                        </div>
                    </section>

                    {/* ── TRUST STRIP ── */}
                    <section className="relative z-10 border-y border-white/[0.06] py-6 px-4">
                        <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-8 text-xs text-white/25 tracking-widest uppercase">
                            <span>No account needed</span>
                            <span className="text-white/10">·</span>
                            <span>No watermark</span>
                            <span className="text-white/10">·</span>
                            <span>PDF never stored</span>
                            <span className="text-white/10">·</span>
                            <span>Instant download</span>
                            <span className="text-white/10">·</span>
                            <span>Print-ready quality</span>
                        </div>
                    </section>

                    {/* ── HOW IT WORKS — H2 with LSI keywords ── */}
                    <section className="relative z-10 max-w-4xl mx-auto px-6 py-14">
                        <h2 className="text-xl font-semibold text-white/80 mb-8 text-center">
                            How to Create a QR Code for Your Menu — Free, in 3 Steps
                        </h2>

                        <div className="grid md:grid-cols-3 gap-5">
                            {[
                                {
                                    step: "01",
                                    title: "Upload Your PDF Menu",
                                    desc: "Drag and drop your existing PDF menu — restaurant, café, hotel room service, or salon service list. Any PDF works.",
                                },
                                {
                                    step: "02",
                                    title: "Customise Your QR Code",
                                    desc: "Pick brand colours, add your logo, and enter your business name. Make your QR menu look as polished as your brand.",
                                },
                                {
                                    step: "03",
                                    title: "Download & Print for Free",
                                    desc: "Download a high-resolution PNG or PDF of your QR code — ready to print on table cards, stickers, or display boards.",
                                },
                            ].map(({ step, title, desc }) => (
                                <div key={step} className="bg-white/[0.03] border border-white/[0.07] rounded-2xl p-5">
                                    <span className="text-3xl font-bold text-white/[0.07] tracking-tighter">{step}</span>
                                    <h3 className="text-sm font-medium text-white/70 mt-3 mb-2">{title}</h3>
                                    <p className="text-xs text-white/35 leading-relaxed">{desc}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* ── WHO IT'S FOR — keyword-rich use case grid ── */}
                    <section className="relative z-10 max-w-4xl mx-auto px-6 pb-14">
                        <h2 className="text-xl font-semibold text-white/80 mb-2 text-center">
                            Free QR Code Menu Generator for Every Business
                        </h2>
                        <p className="text-center text-sm text-white/35 mb-8 max-w-xl mx-auto">
                            Used by restaurants, cafés, hotels, bars, salons, and spas to go contactless instantly.
                        </p>

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {[
                                { icon: "🍽️", title: "Restaurant QR Menu", desc: "Replace paper menus with a scannable QR. Customers view your full menu on their phone — no app needed." },
                                { icon: "☕", title: "Café Menu QR Code", desc: "Share your drinks and snack menu digitally. Update prices or items anytime without reprinting." },
                                { icon: "🏨", title: "Hotel Room Service QR", desc: "Generate a QR code for your hotel room service menu. Guests scan from their room, no printed copy needed." },
                                { icon: "✂️", title: "Salon Service Menu", desc: "Display your hair or beauty service list via a scannable QR at your counter or reception." },
                                { icon: "🍹", title: "Bar & Lounge Menu QR", desc: "Share your cocktail, wine, or drinks menu via QR code. Perfect for table tents or bar placement." },
                                { icon: "💆", title: "Spa Treatment Menu", desc: "Give clients a polished, contactless way to browse your spa and wellness service offerings." },
                            ].map(({ icon, title, desc }) => (
                                <div key={title} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 hover:border-white/[0.12] transition-colors">
                                    <span className="text-xl mb-2 block">{icon}</span>
                                    <h3 className="text-xs font-medium text-white/60 mb-1">{title}</h3>
                                    <p className="text-xs text-white/30 leading-relaxed">{desc}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* ── CTA SECTION ── */}
                    <section className="relative z-10 overflow-hidden px-4 py-20">
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="w-[500px] h-[300px] bg-white/[0.03] rounded-full blur-3xl" />
                        </div>

                        <div className="relative max-w-2xl mx-auto text-center border border-white/[0.08] rounded-3xl p-10 bg-white/[0.02] backdrop-blur-sm">
                            <span className="inline-block text-[10px] text-white/40 tracking-widest uppercase border border-white/10 rounded-full px-3 py-1 mb-5">
                                Power up with Zafflo
                            </span>

                            <h2 className="text-2xl md:text-3xl font-semibold leading-tight text-white">
                                Want customers to order directly<br className="hidden md:block" /> from this QR code?
                            </h2>

                            <p className="mt-3 text-white/40 text-base font-light max-w-md mx-auto">
                                Turn your static QR menu into a full digital ordering system — with payments,
                                customer tracking, and repeat marketing built in.
                            </p>

                            <div className="mt-8 flex justify-center gap-3 flex-wrap">
                                <button
                                    onClick={() => window.open("https://zafflo.com", "_blank")}
                                    className="bg-white text-black px-7 py-2.5 rounded-xl font-medium text-sm 
  hover:bg-white/90 transition-all duration-200 
  hover:scale-[1.01] active:scale-[0.98]"
                                >
                                    Start Taking Orders
                                </button>

                                <button
                                    onClick={() => window.open("https://zafflo.com", "_blank")}
                                    className="border border-white/20 text-white/70 px-7 py-2.5 rounded-xl text-sm 
  hover:border-white/40 hover:text-white 
  transition-all duration-200 
  hover:scale-[1.01] active:scale-[0.98]"
                                >
                                    View Demo
                                </button>
                            </div>
                        </div>
                    </section>

                    {/* ── FAQ — targets "People Also Ask" / featured snippets ── */}
                    <section className="relative z-10 max-w-3xl mx-auto px-6 py-14 border-t border-white/[0.06]">
                        <h2 className="text-xl font-semibold text-white/80 mb-8">
                            Frequently Asked Questions
                        </h2>

                        <div className="space-y-4">
                            {[
                                {
                                    q: "How do I create a free QR code for my restaurant menu?",
                                    a: "Upload your PDF menu using the tool above, customise the QR code with your brand colours and logo, then download it for free — no account or sign-up required. The whole process takes under 60 seconds.",
                                },
                                {
                                    q: "Can I generate a QR code menu without signing up?",
                                    a: "Yes. Zafflo's free online QR menu generator requires no account, no email, and no subscription. Upload your PDF, customise, and download instantly.",
                                },
                                {
                                    q: "What file format do I need to create a QR code menu?",
                                    a: "You need a PDF file of your menu. Most restaurants and cafés already have their menu as a PDF — simply upload it and your QR code is generated automatically.",
                                },
                                {
                                    q: "Is the downloaded QR code menu print-ready?",
                                    a: "Yes. Download your QR code as a high-resolution PNG or PDF, suitable for printing on table cards, menu boards, stickers, or posters.",
                                },
                                {
                                    q: "Does this QR menu generator work for hotels, salons, and spas?",
                                    a: "Absolutely. Any business with a PDF menu or service list — hotels, salons, spas, bars, and cafés — can use this free QR menu generator to go contactless in seconds.",
                                },
                                {
                                    q: "What is the difference between a free QR menu and Zafflo?",
                                    a: "This free tool creates a static QR code that links to your PDF menu. Zafflo goes further — customers can order and pay directly from the same QR code, with analytics and repeat marketing included.",
                                },
                            ].map(({ q, a }) => (
                                <div key={q} className="border border-white/[0.07] rounded-xl p-5 bg-white/[0.02]">
                                    <h3 className="text-sm font-medium text-white/70 mb-2">{q}</h3>
                                    <p className="text-xs text-white/35 leading-relaxed">{a}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* ── SEO EDITORIAL — keyword-dense, naturally written ── */}
                    <section className="relative z-10 max-w-3xl mx-auto px-6 pb-16 border-t border-white/[0.06] pt-12">
                        <h2 className="text-lg font-medium text-white/70 mb-6">
                            Why Restaurants and Cafés Are Switching to QR Code Menus
                        </h2>

                        <div className="space-y-4 text-white/35 text-sm leading-relaxed font-light">
                            <p>
                                A <strong className="text-white/50 font-normal">QR code menu</strong> lets customers access your full menu
                                from their smartphone — no app, no printing, no waiting. Customers scan the QR code at their table
                                and your PDF menu opens directly in their browser. It is one of the fastest, most cost-effective
                                upgrades any food or hospitality business can make.
                            </p>
                            <p>
                                Creating a <strong className="text-white/50 font-normal">free QR code for your restaurant menu</strong> used
                                to require technical knowledge or paid software. With Zafflo's free QR menu generator, any restaurant owner,
                                café manager, hotel operator, or salon owner can generate a custom-branded, downloadable QR menu
                                in under a minute — with no design skills and no cost.
                            </p>
                            <p>
                                Unlike generic QR code generators, this tool is built specifically for menus. Add your business name,
                                match QR colours to your brand, and embed your logo — so your
                                <strong className="text-white/50 font-normal"> digital menu QR code</strong> looks as professional as your food.
                            </p>
                            <p>
                                When you are ready to go beyond a static PDF menu,
                                Zafflo turns your QR code into a complete online ordering system — letting customers order and pay
                                directly from the same QR, with full analytics and marketing automation built in.
                            </p>
                        </div>
                    </section>

                </div>

            </div>
        </>
    );
}